import { createServerFn } from "@tanstack/react-start";
import * as db from "@/lib/db.server";
import { requireAuth } from "@/lib/auth/middleware";
import type { Database } from "@/lib/database.types";
import { cityFromLocation } from "@/lib/location-autocomplete";
import { logger } from "@/lib/logger.browser";

export type Provider = Database["public"]["Tables"]["providers"]["Row"];

export type Insurance = { id: string; name: string; short: string; color: string };

export const INSURANCES: Insurance[] = [
 { id: "bcbs", name: "Blue Cross Blue Shield", short: "BCBS", color: "#0073CF" },
 { id: "aetna", name: "Aetna", short: "Aetna", color: "#7D2A8E" },
 { id: "uhc", name: "UnitedHealthcare", short: "UnitedHealthcare", color: "#002677" },
 { id: "cigna", name: "Cigna", short: "Cigna", color: "#F47A20" },
 { id: "humana", name: "Humana", short: "Humana", color: "#78BE20" },
 { id: "kaiser", name: "Kaiser Permanente", short: "Kaiser Permanente", color: "#006BA6" },
 { id: "anthem", name: "Anthem", short: "Anthem", color: "#0072CE" },
 { id: "molina", name: "Molina Healthcare", short: "Molina", color: "#00A4B4" },
 { id: "centene", name: "Centene", short: "Centene", color: "#582C83" },
 { id: "medicare", name: "Medicare", short: "Medicare", color: "#1A4480" },
 { id: "medicaid", name: "Medicaid", short: "Medicaid", color: "#0B5E3F" },
 { id: "tricare", name: "TRICARE", short: "TRICARE", color: "#003E7E" },
 { id: "ambetter", name: "Ambetter", short: "Ambetter", color: "#00833E" },
 { id: "oscar", name: "Oscar Health", short: "Oscar", color: "#F25C2A" },
 { id: "wellcare", name: "WellCare", short: "WellCare", color: "#0066A1" },
 { id: "amerigroup", name: "Amerigroup", short: "Amerigroup", color: "#005EB8" },
 { id: "fidelis", name: "Fidelis Care", short: "Fidelis", color: "#00529B" },
 { id: "healthnet", name: "Health Net", short: "Health Net", color: "#0085CA" },
 { id: "horizon", name: "Horizon BCBS NJ", short: "Horizon", color: "#003DA5" },
 { id: "highmark", name: "Highmark", short: "Highmark", color: "#005EB8" },
 { id: "independence", name: "Independence Blue Cross", short: "IBX", color: "#003DA5" },
 { id: "carefirst", name: "CareFirst BCBS", short: "CareFirst", color: "#003DA5" },
 { id: "bsca", name: "Blue Shield of California", short: "BSCA", color: "#0072CE" },
 { id: "premera", name: "Premera Blue Cross", short: "Premera", color: "#0072CE" },
 { id: "regence", name: "Regence", short: "Regence", color: "#003DA5" },
 { id: "emblem", name: "EmblemHealth", short: "Emblem", color: "#003DA5" },
 { id: "ghi", name: "GHI", short: "GHI", color: "#003DA5" },
 { id: "metroplus", name: "MetroPlus Health", short: "MetroPlus", color: "#005EB8" },
 { id: "healthfirst", name: "Healthfirst", short: "Healthfirst", color: "#FF6900" },
 { id: "meridian", name: "Meridian", short: "Meridian", color: "#582C83" },
 { id: "buckeye", name: "Buckeye Health Plan", short: "Buckeye", color: "#582C83" },
 { id: "peachstate", name: "Peach State Health Plan", short: "Peach State", color: "#582C83" },
 { id: "superior", name: "Superior HealthPlan", short: "Superior", color: "#582C83" },
 { id: "caresource", name: "CareSource", short: "CareSource", color: "#0085CA" },
 { id: "geha", name: "GEHA", short: "GEHA", color: "#003DA5" },
 { id: "mutual", name: "Mutual of Omaha", short: "Mutual of Omaha", color: "#0072CE" },
 { id: "bright", name: "Bright HealthCare", short: "Bright", color: "#FF6900" },
 { id: "friday", name: "Friday Health Plans", short: "Friday", color: "#F25C2A" },
 { id: "sutter", name: "Sutter Health Plus", short: "Sutter", color: "#0085CA" },
 { id: "providence", name: "Providence Health Plan", short: "Providence", color: "#005EB8" },
 { id: "uphp", name: "University of Pittsburgh Medical Center", short: "UPMC", color: "#003DA5" },
 { id: "harvard", name: "Harvard Pilgrim Health Care", short: "Harvard Pilgrim", color: "#003DA5" },
 { id: "tufts", name: "Tufts Health Plan", short: "Tufts", color: "#582C83" },
 { id: "cdphp", name: "CDPHP", short: "CDPHP", color: "#005EB8" },
 { id: "mvp", name: "MVP Health Care", short: "MVP", color: "#003DA5" },
 { id: "selecthealth", name: "SelectHealth", short: "SelectHealth", color: "#0072CE" },
 { id: "scott", name: "Scott and White Health Plan", short: "Scott & White", color: "#582C83" },
 { id: "bswh", name: "Baylor Scott & White", short: "BSWH", color: "#003DA5" },
 { id: "vahealth", name: "VA Health Care", short: "VA", color: "#003DA5" },
 { id: "champva", name: "CHAMPVA", short: "CHAMPVA", color: "#003DA5" },
 { id: "ihc", name: "Indian Health Service", short: "IHS", color: "#0B5E3F" },
 { id: "selfpay", name: "Self-Pay / No Insurance", short: "Self-Pay", color: "#6B7280" },
];

export const SPECIALTIES = [
 "Allergists / Immunologists",
 "Anesthesiologists",
 "Audiologists",
 "Cardiologists",
 "Chiropractors",
 "Dentists",
 "Dermatologists",
 "Dietitians / Nutritionists",
 "Endocrinologists",
 "ENT (Ear, Nose & Throat)",
 "Eye Doctors / Optometrists",
 "Family Doctors",
 "Gastroenterologists",
 "General Practitioners",
 "Geriatricians",
 "Gynecologists",
 "Hematologists",
 "Infectious Disease Doctors",
 "Internal Medicine",
 "Mental Health / Therapists",
 "Midwives",
 "Naturopathic Doctors",
 "Nephrologists",
 "Neurologists",
 "Nurse Practitioners",
 "Obstetricians",
 "Occupational Therapists",
 "Oncologists",
 "Ophthalmologists",
 "Oral Surgeons",
 "Orthodontists",
 "Orthopedic Surgeons",
 "Pain Management",
 "Pediatricians",
 "Periodontists",
 "Physical Therapy",
 "Plastic Surgeons",
 "Podiatrists",
 "Primary Care",
 "Psychiatrists",
 "Psychologists",
 "Pulmonologists",
 "Radiologists",
 "Rheumatologists",
 "Speech Therapists",
 "Sports Medicine",
 "Surgeons",
 "Urgent Care",
 "Urologists",
 "Veterinarians",
 "Women's Health",
];

/**
 * Send the specialty to the database AS-IS.
 *
 * This used to convert a label into a '|'-joined regex (SPECIALTY_DB_PATTERNS,
 * removed) before every search. It no longer should: resolve_specialty_raws()
 * in the database resolves a canonical slug, a canonical label, a raw taxonomy
 * value, or a synonym ("retina", "root canal", "pediatra") against the curated
 * taxonomy — which is both more accurate than the patterns and aware that
 * endodontists are dentists. Handing it a regex instead of the label opts back
 * into the legacy fallback path and loses all of that.
 * See aws-migration/sql/2026-07-31-specialty-taxonomy.sql.
 *
 * Kept as a named function (rather than inlined at the two call sites) because
 * it is the single documented seam where a specialty crosses into the DB.
 */
export function specialtyToDbPattern(label?: string | null): string | undefined {
 return label?.trim() || undefined;
}

const SPECIALTY_TITLE_MAP: Record<string, string> = {
 "Allergists / Immunologists": "Allergist",
 "Anesthesiologists": "Anesthesiologist",
 "Audiologists": "Audiologist",
 "Cardiologists": "Cardiologist",
 "Chiropractors": "Chiropractor",
 "Dentists": "Dentist",
 "Dermatologists": "Dermatologist",
 "Dietitians / Nutritionists": "Dietitian",
 "Endocrinologists": "Endocrinologist",
 "ENT (Ear, Nose & Throat)": "ENT Specialist",
 "Eye Doctors / Optometrists": "Optometrist",
 "Family Doctors": "Family Doctor",
 "Gastroenterologists": "Gastroenterologist",
 "General Practitioners": "General Practitioner",
 "Geriatricians": "Geriatrician",
 "Gynecologists": "Gynecologist",
 "Hematologists": "Hematologist",
 "Infectious Disease Doctors": "Infectious Disease Doctor",
 "Internal Medicine": "Internal Medicine Doctor",
 "Mental Health / Therapists": "Mental Health Therapist",
 "Midwives": "Midwife",
 "Naturopathic Doctors": "Naturopathic Doctor",
 "Nephrologists": "Nephrologist",
 "Neurologists": "Neurologist",
 "Nurse Practitioners": "Nurse Practitioner",
 "Obstetricians": "Obstetrician",
 "Occupational Therapists": "Occupational Therapist",
 "Oncologists": "Oncologist",
 "Ophthalmologists": "Ophthalmologist",
 "Oral Surgeons": "Oral Surgeon",
 "Orthodontists": "Orthodontist",
 "Orthopedic Surgeons": "Orthopedic Surgeon",
 "Pain Management": "Pain Management Specialist",
 "Pediatricians": "Pediatrician",
 "Periodontists": "Periodontist",
 "Physical Therapy": "Physical Therapist",
 "Plastic Surgeons": "Plastic Surgeon",
 "Podiatrists": "Podiatrist",
 "Primary Care": "Primary Care Doctor",
 "Psychiatrists": "Psychiatrist",
 "Psychologists": "Psychologist",
 "Pulmonologists": "Pulmonologist",
 "Radiologists": "Radiologist",
 "Rheumatologists": "Rheumatologist",
 "Speech Therapists": "Speech Therapist",
 "Sports Medicine": "Sports Medicine Doctor",
 "Surgeons": "Surgeon",
 "Urgent Care": "Urgent Care Provider",
 "Urologists": "Urologist",
 "Veterinarians": "Veterinarian",
 "Women's Health": "Women's Health Specialist",
};

export function specialtyToTitle(label?: string | null): string {
 const raw = (label ?? "").trim();
 if (!raw) return "Healthcare Provider";
 if (SPECIALTY_TITLE_MAP[raw]) return SPECIALTY_TITLE_MAP[raw];
 return raw
  .replace(/ists\b/gi, "ist")
  .replace(/ians\b/gi, "ian")
  .replace(/ors\b/gi, "or")
  .replace(/eons\b/gi, "eon");
}

export function providerDisplayText(value?: string | null): string {
 if (!value) return "";
 return value
 .replace(/[‐‑‒ ―]+/g, " ")
 .replace(/\b([a-z0-9]+)-([a-z0-9]+)\b/gi, "$1 $2")
 .replace(/\s{2,}/g, " ")
 .trim();
}

export const SERVICES = [
 "General Checkup", "Cleaning", "Whitening", "Fillings", "Crowns", "Bridges",
 "Implants", "Root Canal", "Extractions", "Veneers", "Braces", "Invisalign",
 "Pediatric Care", "Cosmetic Dentistry", "Emergency Care", "Sedation",
 "Telehealth Visit", "Annual Physical", "Vaccinations", "Lab Work",
 "Allergy Testing", "Dermatology Consult", "Skin Screening", "Therapy Session",
 "Chiropractic Adjustment", "Physical Therapy", "Massage Therapy",
 "Eye Exam", "Glasses Fitting", "Contact Lens Fitting", "Hearing Test",
 "Prenatal Care", "Women's Wellness", "Men's Wellness", "Diabetes Management",
 "Weight Management", "Nutrition Counseling", "Smoking Cessation",
 "Mental Health Counseling", "Sleep Study", "Sports Injury Care",
];

export const LANGUAGES = [
 "English", "Spanish", "French", "Portuguese", "Italian", "German",
 "Mandarin", "Cantonese", "Japanese", "Korean", "Vietnamese", "Tagalog",
 "Hindi", "Urdu", "Punjabi", "Bengali", "Arabic", "Hebrew", "Farsi",
 "Russian", "Polish", "Ukrainian", "Romanian", "Turkish", "Greek",
 "Dutch", "Swedish", "Norwegian", "ASL (American Sign Language)",
];

export const CITIES = [
 "Arlington, TX", "Dallas, TX", "Houston, TX", "Austin, TX", "Phoenix, AZ",
 "Los Angeles, CA", "San Diego, CA", "Chicago, IL", "Miami, FL",
 "New York, NY", "Seattle, WA", "Denver, CO",
];

export const CONDITIONS = [
 "Back pain", "Anxiety & depression", "Skin conditions", "Diabetes",
 "Pregnancy care", "Allergies", "Sports injuries", "Cavities & cleanings",
];

export function findInsurance(id: string) {
 return INSURANCES.find((i) => i.id === id);
}

export function parseAiQuery(q: string) {
 const lower = q.toLowerCase();
 const specialty = SPECIALTIES.find((s) => lower.includes(s.toLowerCase().slice(0, -1)));
 const city = CITIES.find((c) => lower.includes(c.split(",")[0].toLowerCase()));
 // Whole words only: a bare `includes` matched short carrier codes inside
 // ordinary words — "Nadeem Vaidya" contains "va", so a name search came back
 // filtered to the VA carrier.
 const hasWord = (needle: string) =>
 new RegExp(`\\b${needle.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "i").test(q);
 const insurance = INSURANCES.find((i) => hasWord(i.short) || hasWord(i.name));
 return {
 specialty: specialty || "",
 location: city || "",
 insurance: insurance?.short || "",
 };
}

// Higher rank = shown first. Mirrors the SQL provider_rank() helper.
export function rankProvider(p: Pick<Provider, "is_featured" | "featured_subscription_status" | "listing_status" | "plan">) {
 if (p.is_featured && p.featured_subscription_status === "active") return 4;
 if (p.listing_status === "premium" || p.plan === "premium_monthly" || p.plan === "premium_yearly") return 3;
 if (p.listing_status === "claimed" || p.listing_status === "claimed_free") return 2;
 return 1;
}

const LAST_SEARCH_RESULTS_KEY = "heallexa_last_search_results";

function readCachedSearchResults(): Provider[] {
 if (typeof window === "undefined") return [];
 try {
 const cached = window.localStorage.getItem(LAST_SEARCH_RESULTS_KEY);
 if (!cached) return [];
 const parsed = JSON.parse(cached) as { results?: Provider[] };
 return Array.isArray(parsed.results) ? parsed.results : [];
 } catch {
 return [];
 }
}

export function cacheLastSearchResults(results: Provider[]) {
 if (typeof window === "undefined" || results.length === 0) return;
 try {
 window.localStorage.setItem(
 LAST_SEARCH_RESULTS_KEY,
 JSON.stringify({ savedAt: new Date().toISOString(), results: results.slice(0, 30) }),
 );
 } catch { /* local cache is best-effort */ }
}

// In-memory LRU-ish cache keyed by query params; survives the tab session.
const SEARCH_CACHE_TTL_MS = 2 * 60 * 1000; // 2 minutes
const SEARCH_CACHE_MAX = 50;
const SEARCH_RESULT_LIMIT = 20;
const searchCache = new Map<string, { at: number; rows: Provider[] }>();

const US_STATE_NAME_TO_CODE_DATA: Record<string, string> = {
 alabama:"AL",alaska:"AK",arizona:"AZ",arkansas:"AR",california:"CA",colorado:"CO",
 connecticut:"CT",delaware:"DE",florida:"FL",georgia:"GA",hawaii:"HI",idaho:"ID",
 illinois:"IL",indiana:"IN",iowa:"IA",kansas:"KS",kentucky:"KY",louisiana:"LA",
 maine:"ME",maryland:"MD",massachusetts:"MA",michigan:"MI",minnesota:"MN",
 mississippi:"MS",missouri:"MO",montana:"MT",nebraska:"NE",nevada:"NV",
 "new hampshire":"NH","new jersey":"NJ","new mexico":"NM","new york":"NY",
 "north carolina":"NC","north dakota":"ND",ohio:"OH",oklahoma:"OK",oregon:"OR",
 pennsylvania:"PA","rhode island":"RI","south carolina":"SC","south dakota":"SD",
 tennessee:"TN",texas:"TX",utah:"UT",vermont:"VT",virginia:"VA",washington:"WA",
 "west virginia":"WV",wisconsin:"WI",wyoming:"WY","district of columbia":"DC",
};
const US_STATE_CODES_SET = new Set(Object.values(US_STATE_NAME_TO_CODE_DATA));

export function parseLocationToCityState(location?: string): { city?: string; state?: string } {
 if (!location) return {};
 const raw = location.trim();
 if (!raw) return {};

 let state: string | undefined;
 let cityPart = raw;

 // 0) The whole input is a US state (full name or 2-letter code) — treat it as
 // a STATE filter, not a city. Without this, "Alabama" was parsed as a city
 // (city="Alabama"), so the state-page "Search all providers in {state}" link
 // queried lower(city)='alabama' and returned 0 results.
 const rawLower = raw.toLowerCase();
 if (US_STATE_NAME_TO_CODE_DATA[rawLower]) {
  return { state: US_STATE_NAME_TO_CODE_DATA[rawLower] };
 }
 if (/^[A-Za-z]{2}$/.test(raw) && US_STATE_CODES_SET.has(raw.toUpperCase())) {
  return { state: raw.toUpperCase() };
 }

 // 1) Trailing full state name: "Arlington, Texas" / "Arlington Texas"
 for (const [name, code] of Object.entries(US_STATE_NAME_TO_CODE_DATA)) {
  const re = new RegExp(`[,\\s]+${name.replace(/\s+/g, "\\s+")}\\s*$`, "i");
  if (re.test(raw)) {
   state = code;
   cityPart = raw.replace(re, "").trim();
   break;
  }
 }

 // 2) Trailing 2-letter state code, with or without comma: "Arlington, TX" / "Arlington TX"
 if (!state) {
  const m = raw.match(/^(.*?)[,\s]+([A-Za-z]{2})\s*$/);
  if (m && US_STATE_CODES_SET.has(m[2].toUpperCase())) {
   state = m[2].toUpperCase();
   cityPart = m[1].trim();
  }
 }

 // 3) Fallback: comma split
 if (!state) {
  const parts = raw.split(",").map((s) => s.trim()).filter(Boolean);
  cityPart = parts[0] || raw;
  if (parts.length > 1) {
   const tail = parts.slice(1).join(" ");
   const mm = tail.match(/\b([A-Za-z]{2})\b/);
   if (mm && US_STATE_CODES_SET.has(mm[1].toUpperCase())) state = mm[1].toUpperCase();
  }
 }

 const city = cityPart.replace(/^,+|,+$/g, "").trim() || undefined;
 return { city, state };
}

export type GeocodeResult = { lat: number | null; lng: number | null; city?: string; state?: string };

// Geocode cache keyed by raw location string
const geocodeMemCache = new Map<string, { at: number; result: GeocodeResult }>();
const GEOCODE_TTL_MS = 30 * 60 * 1000;

// Public read (no auth) — resolves a free-text location via the geocode_location
// DB helper. RETURNS TABLE(latitude, longitude, city, state, zip, source); we
// also read city/state so a bare ZIP can be turned into real city/state filters.
const geocodeLocationFn = createServerFn({ method: "POST" })
 .inputValidator((d: { query: string }) => d)
 .handler(async ({ data }): Promise<GeocodeResult> => {
  const rows = await db.query(
   "SELECT latitude, longitude, city, state FROM public.geocode_location($1)",
   [data.query],
  );
  const row = rows[0] as
   | { latitude: number | null; longitude: number | null; city: string | null; state: string | null }
   | undefined;
  return {
   lat: row?.latitude ?? null,
   lng: row?.longitude ?? null,
   city: row?.city ?? undefined,
   state: row?.state ?? undefined,
  };
 });

export async function geocodeLocation(location: string): Promise<GeocodeResult> {
 const key = location.trim().toLowerCase();
 if (!key) return { lat: null, lng: null };
 const hit = geocodeMemCache.get(key);
 if (hit && Date.now() - hit.at < GEOCODE_TTL_MS) return hit.result;
 try {
  const result = await geocodeLocationFn({ data: { query: location } });
  geocodeMemCache.set(key, { at: Date.now(), result });
  return result;
 } catch {
  return { lat: null, lng: null };
 }
}

/**
 * The 5-digit ZIP in a location string, or undefined.
 *
 * Anchored to the END of the string because that is where a ZIP goes in a US
 * address ("Houston, TX 77002", "77002-1234", "77002"). Anchoring matters: a
 * bare `\d{5}` anywhere would read "12345 Main St" as ZIP 12345 and silently
 * relocate the search. The optional +4 is matched and discarded —
 * geocode_location() keys on the 5-digit prefix.
 */
export function extractZip(location: string | undefined): string | undefined {
 const m = location?.trim().match(/(?:^|[\s,])(\d{5})(?:-\d{4})?$/);
 return m?.[1];
}

/**
 * City/state/coords to send to search_providers_v3 for a free-text location.
 *
 * A bare 5-digit ZIP is NOT a city: `_city = '77002'` compiles to
 * `lower(city) = '77002'` and matched zero rows even though ZIP 77002 has 858
 * approved providers. geocode_location() already resolves a ZIP to its
 * city/state/lat/lng, so resolve it here — the one place both the search and
 * the count build their args — instead of at each call site.
 * `geocode` is injectable for tests.
 */
export async function resolveLocationArgs(
 location: string | undefined,
 stateOverride: string | undefined,
 geocode: (q: string) => Promise<GeocodeResult> = geocodeLocation,
): Promise<{ city?: string; state?: string; coords: { lat: number | null; lng: number | null } }> {
 const parsed = parseLocationToCityState(location);
 // Only fall back to cityFromLocation when the location isn't a bare state.
 // parseLocationToCityState returns {state} (no city) for "Alabama"; using
 // cityFromLocation there would wrongly set _city="Alabama" and return 0 rows.
 let city = parsed.city || (location && !parsed.state ? cityFromLocation(location) : undefined);
 let state = (stateOverride || parsed.state)?.toUpperCase();
 let coords: { lat: number | null; lng: number | null } = { lat: null, lng: null };

 // Any location ENDING in a ZIP, not just a bare one. "77002-1234" and
 // "Houston, TX 77002" used to miss this branch entirely and were sent as
 // _city="77002-1234" / _city="Houston" — the first matched nothing, the
 // second silently widened a ZIP search to the whole city with no centroid,
 // so no distances and no nearest-first ordering.
 const zip = extractZip(location);
 if (zip) {
  const geo = await geocode(zip);
  coords = { lat: geo.lat, lng: geo.lng };
  // Unresolvable ZIP (~2%): leave the digits as _city so the search returns
  // empty rather than silently widening to a nationwide result set. Only do
  // that when the ZIP was the WHOLE input — "Houston, TX 99999" still has a
  // perfectly good city/state to fall back on.
  if (geo.city) {
   city = geo.city;
   state = (stateOverride || geo.state || state)?.toUpperCase();
  } else if (zip === location?.trim()) {
   city = zip;
  }
 }
 return { city, state, coords };
}

/** Default radius (miles) when the caller didn't pick one, for both search and count.
 *
 * 25mi whenever we have a centre to measure from — device "near me" OR a
 * searched ZIP's centroid. Both are "show me what's near this point".
 *
 * A ZIP used to be excluded here: the old radius branch PK-looked-up every hit
 * in the 6M-row providers table just to read is_active/moderation_status, so a
 * 25mi ZIP search took ~57s against a 5s statement_timeout and came back empty.
 * 2026-08-12-search-radius-two-phase.sql moved those predicates into
 * provider_coords and made the branch an index-ordered KNN scan over a bounded
 * pool, so distance scoping is affordable now and a ZIP search means what it
 * says instead of quietly degrading to a city search.
 *
 * An EXPLICIT 0 means "no distance scoping" and is honored — that's how the
 * loader's nationwide/loose-city fallbacks escape an empty radius search.
 */
export function defaultRadiusMiles(
 radiusMiles: number | undefined,
 hasCenter: boolean,
): number {
 if (radiusMiles != null) return radiusMiles > 0 ? Math.min(radiusMiles, 50) : 0;
 return hasCenter ? 25 : 0;
}

export type SearchMeta = {
 requestedRadiusMiles: number;
 usedRadius: boolean;
 centroid: { lat: number; lng: number } | null;
 city?: string;
 state?: string;
};

export async function searchProvidersWithMeta(opts: {
 specialty?: string; location?: string; insurance?: string; q?: string;
 limit?: number; offset?: number;
 state?: string; radiusMiles?: number;
 // Device geolocation ("near me"). When present, used directly as the radius
 // center (no geocoding) and as the reference point for per-provider distance.
 lat?: number | null; lng?: number | null;
 // The visitor's coordinates when they must NOT move the search centre — the
 // /providers/... landing pages, whose city is baked into the URL, title, and
 // canonical. Passing those coords as `lat`/`lng` would recentre an
 // "Austin, TX" page on a visitor sitting in Boston. These stamp distance_m
 // only, so the page keeps its own scope and every card still says how far it
 // is from the patient.
 distanceLat?: number | null; distanceLng?: number | null;
}): Promise<{ results: Provider[]; meta: SearchMeta }> {
 let insuranceId: string | null = null;
 if (opts.insurance) {
 const ins = INSURANCES.find((i) =>
 i.name.toLowerCase().includes(opts.insurance!.toLowerCase()) ||
 opts.insurance!.toLowerCase().includes(i.short.toLowerCase().split(" ")[0])
 );
 insuranceId = ins?.id ?? null;
 }
 const resolved = await resolveLocationArgs(opts.location, opts.state);
 const stateArg = resolved.state;
 const cityArg = resolved.city;
 // Device "near me" and a searched ZIP both default to a 25mi radius when the
 // caller didn't pick one — see defaultRadiusMiles(). A ZIP's centre is its
 // centroid from resolveLocationArgs, which is why it counts as a centre here.
 const deviceCoords = opts.lat != null && opts.lng != null;
 const requestedRadius = defaultRadiusMiles(
  opts.radiusMiles,
  deviceCoords || resolved.coords.lat != null,
 );

 // For radius search, geocode the location into lat/lng so search_providers_v3
 // can filter by true haversine distance. Try several spellings so a bare
 // "Arlington" doesn't resolve to Arlington, WI when the user means
 // Arlington, TX. Device coords skip geocoding entirely.
 let lat: number | null = deviceCoords ? opts.lat! : resolved.coords.lat;
 let lng: number | null = deviceCoords ? opts.lng! : resolved.coords.lng;
 if (!deviceCoords && requestedRadius > 0 && lat == null) {
  const candidates: string[] = [];
  const push = (s?: string | null) => {
   if (!s) return;
   const t = s.trim();
   if (t && !candidates.includes(t)) candidates.push(t);
  };
  if (cityArg && stateArg) push(`${cityArg}, ${stateArg}`);
  if (opts.location) push(opts.location);
  if (cityArg && stateArg && cityArg.toLowerCase() !== opts.location?.toLowerCase()) {
   push(`${cityArg} ${stateArg}`);
  }
  if (cityArg) push(cityArg);
  for (const cand of candidates) {
   const geo = await geocodeLocation(cand);
   if (geo.lat != null && geo.lng != null) {
    lat = geo.lat;
    lng = geo.lng;
    break;
   }
  }
 }

 const usedRadius = requestedRadius > 0 && lat != null && lng != null;

 const params: Record<string, unknown> = {
 _q: opts.q?.trim() || undefined,
 _city: cityArg || undefined,
 _specialty: specialtyToDbPattern(opts.specialty) || undefined,
 _insurance: insuranceId ?? undefined,
   // Allow up to 100 per call so callers can ask for PAGE_SIZE+1 (21) to
   // detect hasMore, while keeping a hard ceiling to protect the DB.
   _limit: Math.min(Math.max(opts.limit ?? SEARCH_RESULT_LIMIT, 1), 100),
 _offset: Math.max(opts.offset ?? 0, 0),
 _state: stateArg || undefined,
 _radius_miles: usedRadius ? requestedRadius : 0,
 // Sent even with _radius_miles = 0. With no radius the RPC ignores them
 // (v_use_radius needs radius > 0) and still runs the fast city branch, but
 // searchProviderRowsFn's attachDistances uses them to stamp distance_m on the
 // page's ~21 rows via a bounded provider_coords lookup. That is what makes a
 // ZIP search show "x mi away" and sort nearest-first instead of by rating.
 _lat: lat ?? undefined,
 _lng: lng ?? undefined,
 // Distance reference, when it differs from the centre. Never reaches the
 // RPC — searchProviderRowsFn strips it and hands it to attachDistances.
 _dist_lat: opts.distanceLat ?? undefined,
 _dist_lng: opts.distanceLng ?? undefined,
 };
 const meta: SearchMeta = {
  requestedRadiusMiles: requestedRadius,
  usedRadius,
  centroid: lat != null && lng != null ? { lat, lng } : null,
  city: cityArg,
  state: stateArg,
 };

 const key = JSON.stringify(params);
 const hit = searchCache.get(key);
 if (hit && Date.now() - hit.at < SEARCH_CACHE_TTL_MS) return { results: hit.rows, meta };

 let rows: unknown[] = [];
 try {
  const { searchProviderRowsFn } = await import("@/lib/search-providers.functions");
  const response = await searchProviderRowsFn({ data: params as any });
  rows = response.rows ?? [];
 } catch (error) {
  // IMPORTANT: do NOT fall back to localStorage-cached results from a
  // previous unrelated query. Doing so served e.g. "Chiropractors in Fort
  // Worth" on a "Cardiologists in Dallas" page. On search failure return an
  // empty set with meta so the page can render its empty state honestly.
  logger.error("search_providers_v3 failed", { params, error });
  return { results: [], meta };
 }
 const results = (rows as Provider[]).map(cleanProviderForDisplay);
 cacheLastSearchResults(results);
 searchCache.set(key, { at: Date.now(), rows: results });
 if (searchCache.size > SEARCH_CACHE_MAX) {
 const oldestKey = searchCache.keys().next().value;
 if (oldestKey) searchCache.delete(oldestKey);
 }
 return { results, meta };
}

// Cap for the count query — keeps it fast on the 6M-row providers table.
// Values >= COUNT_CAP are shown as "COUNT_CAP+" in the UI.
export const SEARCH_COUNT_CAP = 5000;

export async function countProvidersWithFilters(opts: {
 specialty?: string; location?: string; insurance?: string; q?: string;
 state?: string; radiusMiles?: number;
 lat?: number | null; lng?: number | null;
}): Promise<{ count: number; capped: boolean }> {
 let insuranceId: string | null = null;
 if (opts.insurance) {
  const ins = INSURANCES.find((i) =>
   i.name.toLowerCase().includes(opts.insurance!.toLowerCase()) ||
   opts.insurance!.toLowerCase().includes(i.short.toLowerCase().split(" ")[0])
  );
  insuranceId = ins?.id ?? null;
 }
 const resolved = await resolveLocationArgs(opts.location, opts.state);
 const stateArg = resolved.state;
 const cityArg = resolved.city;
 // Must match searchProvidersWithMeta's rule exactly, or the count is taken
 // from a different branch than the results it labels.
 const deviceCoords = opts.lat != null && opts.lng != null;
 const requestedRadius = defaultRadiusMiles(
  opts.radiusMiles,
  deviceCoords || resolved.coords.lat != null,
 );

 let lat: number | null = deviceCoords ? opts.lat! : resolved.coords.lat;
 let lng: number | null = deviceCoords ? opts.lng! : resolved.coords.lng;
 if (!deviceCoords && requestedRadius > 0 && lat == null) {
  const cand = cityArg && stateArg ? `${cityArg}, ${stateArg}` : (opts.location || cityArg || "");
  if (cand) {
   const geo = await geocodeLocation(cand);
   lat = geo.lat; lng = geo.lng;
  }
 }
 const usedRadius = requestedRadius > 0 && lat != null && lng != null;

 const params: Record<string, unknown> = {
  _q: opts.q?.trim() || undefined,
  _city: cityArg || undefined,
  _specialty: specialtyToDbPattern(opts.specialty) || undefined,
  _insurance: insuranceId ?? undefined,
  _state: stateArg || undefined,
  _radius_miles: usedRadius ? requestedRadius : 0,
  _lat: usedRadius ? lat! : undefined,
  _lng: usedRadius ? lng! : undefined,
  _cap: SEARCH_COUNT_CAP,
 };
 try {
  const { countProviderRowsFn } = await import("@/lib/search-providers.functions");
  const { count } = await countProviderRowsFn({ data: params as any });
  const n = Number(count) || 0;
  return { count: n, capped: n >= SEARCH_COUNT_CAP };
 } catch (e) {
  logger.error("search_providers_v3_count threw", { error: e });
  return { count: 0, capped: false };
 }
}

export async function searchProviders(opts: Parameters<typeof searchProvidersWithMeta>[0]) {
 const { results } = await searchProvidersWithMeta(opts);
 return results;
}

export function cleanProviderForDisplay<T extends Provider>(provider: T): T {
 return {
 ...provider,
 name: providerDisplayText(provider.name),
 practice: providerDisplayText(provider.practice),
 specialty: providerDisplayText(provider.specialty),
 city: providerDisplayText(provider.city),
 };
}

// Direct lookup by name / practice / NPI / license number. Runs server-side
// via a secured server function — the providers table's npi/license_number
// columns are restricted to service_role only, so the client cannot query
// them directly. The server fn returns display-safe columns (no NPI,
// license, fax, or claim_token in the response).
export async function findProvidersByIdentifier(query: string, limit = 20) {
  const raw = query.trim();
  if (raw.length < 2) return [] as Provider[];
  const { findProvidersByIdentifierFn } = await import("@/lib/providers-secure.functions");
  const { rows } = await findProvidersByIdentifierFn({ data: { query: raw, limit } });
  return ((rows ?? []) as unknown as Provider[]).map(cleanProviderForDisplay);
}


// Public read (no auth) — slim featured-provider carousel.
const getFeaturedProvidersFn = createServerFn({ method: "GET" })
 .inputValidator((d: { limit: number }) => d)
 .handler(async ({ data }) => {
  // Slim select: the providers row has 119 columns. Featured carousels only
  // need display fields, so avoid shipping unused JSON on every page view.
  const rows = await db.query(
   `SELECT id, public_id, name, credentials, specialty, practice, city, state, zip, address, phone, website, rating, google_rating, google_review_count, photos, profile_photo_url, display_photo_url, has_custom_photo, avatar_gender, gender, listing_status, plan, is_featured, featured_subscription_status, availability, available_days, insurance
    FROM public.providers
    WHERE is_featured = true AND featured_subscription_status = 'active'
    ORDER BY rating DESC NULLS LAST
    LIMIT $1`,
   [data.limit],
  );
  return rows;
 });

export async function getFeaturedProviders(limit = 12) {
 const data = await getFeaturedProvidersFn({ data: { limit } });
 return ((data ?? []) as unknown as Provider[]).map(cleanProviderForDisplay);
}

// Public write (no auth) — fire-and-forget analytics counter. The DB helper
// validates the metric name, so an arbitrary string can't widen the surface.
const trackListingMetricFn = createServerFn({ method: "POST" })
 .inputValidator((d: { providerId: string; metric: string }) => d)
 .handler(async ({ data }) => {
  await db.query(
   "SELECT public.increment_listing_metric($1, $2)",
   [data.providerId, data.metric],
  );
  return { ok: true };
 });

export async function trackListingMetric(providerId: string, metric: "profile_views" | "website_clicks" | "phone_clicks" | "booking_clicks" | "claim_clicks") {
 try {
 await trackListingMetricFn({ data: { providerId, metric } });
 } catch { /* analytics is fire-and-forget */ }
}

// Public read (no auth) — single provider by id.
const getProviderFn = createServerFn({ method: "GET" })
 .inputValidator((d: { id: string }) => d)
 .handler(async ({ data }) => {
  const provider = await db.one("SELECT * FROM public.providers WHERE id = $1", [data.id]);
  return provider;
 });

export async function getProvider(id: string) {
 const data = await getProviderFn({ data: { id } });
 return data ? cleanProviderForDisplay(data as Provider) : null;
}

export type Review = Database["public"]["Tables"]["reviews"]["Row"];

// Public read (no auth) — reviews for a provider, newest first.
const getProviderReviewsFn = createServerFn({ method: "GET" })
 .inputValidator((d: { providerId: string }) => d)
 .handler(async ({ data }) => {
  const rows = await db.query(
   "SELECT * FROM public.reviews WHERE provider_id = $1 ORDER BY review_date DESC",
   [data.providerId],
  );
  return rows;
 });

export async function getProviderReviews(providerId: string) {
 const data = await getProviderReviewsFn({ data: { providerId } });
 return data || [];
}

// User-scoped write (requireAuth) — the reviewer is always the authenticated
// user (owner column user_id set from context, never a client-passed id).
const submitReviewFn = createServerFn({ method: "POST" })
 .middleware([requireAuth])
 .inputValidator((d: { providerId: string; rating: number; comment: string; reviewerName: string }) => d)
 .handler(async ({ data, context }) => {
  const row = await db.one(
   `INSERT INTO public.reviews (provider_id, user_id, source, reviewer_name, rating, comment)
    VALUES ($1, $2, 'internal', $3, $4, $5)
    RETURNING *`,
   [data.providerId, context.userId, data.reviewerName || "Anonymous", data.rating, data.comment],
  );
  return row;
 });

export async function submitReview(input: {
 providerId: string;
 rating: number;
 comment: string;
 reviewerName: string;
}) {
 return submitReviewFn({ data: input });
}

// User-scoped delete (requireAuth) — only the owning user may delete their
// review; scope by user_id from context so a client can't delete others' rows.
const deleteReviewFn = createServerFn({ method: "POST" })
 .middleware([requireAuth])
 .inputValidator((d: { reviewId: string }) => d)
 .handler(async ({ data, context }) => {
  const deleted = await db.execute(
   "DELETE FROM public.reviews WHERE id = $1 AND user_id = $2",
   [data.reviewId, context.userId],
  );
  return { deleted };
 });

export async function deleteReview(reviewId: string) {
 await deleteReviewFn({ data: { reviewId } });
}

